import { ApiConfig } from './config';
import { handleResponse } from './config';
import { AdminStats, Module, Lesson, User, TypeDistribution } from '../../types/admin';


export class AdminService {
  constructor(private config: ApiConfig) {}
  
  // Helper method to get the config (for debugging)
  getConfig() {
    return { ...this.config };
  }

  async getStats(): Promise<AdminStats> {
    console.log('Fetching admin stats from:', `${this.config.baseUrl}/admin/stats`);
    
    const response = await fetch(`${this.config.baseUrl}/admin/stats`, {
      headers: this.config.headers
    });
    console.log('Stats response status:', response.status);
    
    const data = await handleResponse<AdminStats>(response);
    console.log('Stats data received:', data);
    return data;
  }

  async getModules(): Promise<Module[]> {
    console.log('Fetching modules from:', `${this.config.baseUrl}/modules`);
    try {
      const response = await fetch(`${this.config.baseUrl}/modules`, {
        headers: this.config.headers
      });
      console.log('Modules response status:', response.status);
      const data = await handleResponse<Module[]>(response);
      console.log('Modules data received:', data);
      return data;
    } catch (error) {
      console.error('Error fetching modules:', error);
      throw error;
    }
  }

  async createModule(moduleData: Partial<Module>): Promise<Module> {
    const response = await fetch(`${this.config.baseUrl}/admin/modules`, {
      method: 'POST',
      headers: this.config.headers,
      body: JSON.stringify(moduleData)
    });
    return handleResponse<Module>(response);
  }

  async updateModule(moduleId: string, moduleData: Partial<Module>): Promise<Module> {
    const response = await fetch(`${this.config.baseUrl}/admin/modules/${moduleId}`, {
      method: 'PUT',
      headers: this.config.headers,
      body: JSON.stringify(moduleData)
    });
    return handleResponse<Module>(response);
  }

  async deleteModule(moduleId: string): Promise<{ message: string }> {
    const response = await fetch(`${this.config.baseUrl}/admin/modules/${moduleId}`, {
      method: 'DELETE',
      headers: this.config.headers
    });
    return handleResponse<{ message: string }>(response);
  }

  async getLessons(moduleId: string): Promise<Lesson[]> {
    const response = await fetch(`${this.config.baseUrl}/modules/${moduleId}/lessons`, {
      headers: this.config.headers
    });
    return handleResponse<Lesson[]>(response);
  }

  async createLesson(moduleId: string, lessonData: Partial<Lesson>): Promise<Lesson> {
    const response = await fetch(`${this.config.baseUrl}/admin/modules/${moduleId}/lessons`, {
      method: 'POST',
      headers: this.config.headers,
      body: JSON.stringify(lessonData)
    });
    return handleResponse<Lesson>(response);
  }

  async updateLesson(lessonId: string, lessonData: Partial<Lesson>): Promise<Lesson> {
    const response = await fetch(`${this.config.baseUrl}/admin/lessons/${lessonId}`, {
      method: 'PUT',
      headers: this.config.headers,
      body: JSON.stringify(lessonData)
    });
    return handleResponse<Lesson>(response);
  }

  async deleteLesson(lessonId: string): Promise<{ message: string }> {
    const response = await fetch(`${this.config.baseUrl}/admin/lessons/${lessonId}`, {
      method: 'DELETE',
      headers: this.config.headers
    });
    return handleResponse<{ message: string }>(response);
  }

  async getUsers(options?: { sort?: string; filter?: string; type?: string }): Promise<User[]> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (options?.sort) queryParams.append('sort', options.sort);
      if (options?.filter) queryParams.append('filter', options.filter);
      if (options?.type) queryParams.append('type', options.type);
      
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      
      const fullUrl = `${this.config.baseUrl}/admin/users${queryString}`;
      console.log('Fetching users from:', fullUrl);
      console.log('With headers:', JSON.stringify(this.config.headers));
      
      const response = await fetch(fullUrl, {
        headers: this.config.headers,
        credentials: 'include' // Include cookies and auth headers
      });
      
      console.log('Users response status:', response.status);
      console.log('Response headers:', JSON.stringify(Object.fromEntries([...response.headers])));
      
      // If the response is not ok, don't return mock data - throw an error instead
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`Error fetching users: ${response.status} ${response.statusText}. Details: ${errorText}`);
      }
      
      // Make sure we parse the data properly
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing JSON:', parseError);
        throw new Error('Failed to parse server response. The server might be returning invalid JSON.');
      }
      
      console.log('Users data received:', data);
      
      // Add fallback properties for incomplete user data
      const processedData = Array.isArray(data) ? data.map(user => ({
        ...user,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        hasCompletedTest: typeof user.hasCompletedTest === 'boolean' ? user.hasCompletedTest : false,
        enneagramType: user.enneagramType || null,
        role: user.role || 'user',
        createdAt: user.createdAt || new Date().toISOString()
      })) : [];
      
      return processedData;
    } catch (error) {
      console.error('Error fetching users:', error);
      // We're not falling back to mock data - let the component handle the error
      throw error;
    }
  }

  async updateUserRole(userId: string, role: string): Promise<User> {
    const response = await fetch(`${this.config.baseUrl}/admin/users/${userId}/role`, {
      method: 'PATCH',
      headers: this.config.headers,
      body: JSON.stringify({ role })
    });
    return handleResponse<User>(response);
  }
  
  async getUserTestResults(userId: string): Promise<any> {
    console.log('Fetching user test results from:', `${this.config.baseUrl}/admin/users/${userId}/test-results`);
    try {
      const response = await fetch(`${this.config.baseUrl}/admin/users/${userId}/test-results`, {
        headers: this.config.headers
      });
      console.log('User test results response status:', response.status);
      const data = await handleResponse<any>(response);
      console.log('User test results data received:', data);
      return data;
    } catch (error) {
      console.error('Error fetching user test results:', error);
      throw error;
    }
  }

  async getTypeDistribution(): Promise<TypeDistribution[]> {
    console.log('Fetching type distribution from:', `${this.config.baseUrl}/admin/stats/types`);
    
    const response = await fetch(`${this.config.baseUrl}/admin/stats/types`, {
      headers: this.config.headers
    });
    console.log('Type distribution response status:', response.status);
    
    const data = await handleResponse<TypeDistribution[]>(response);
    console.log('Type distribution data received:', data);
    return data;
  }
  
  async getUserProgress(): Promise<any[]> {
    console.log('Fetching user progress from:', `${this.config.baseUrl}/admin/stats/progress`);
    
    try {
      const response = await fetch(`${this.config.baseUrl}/admin/stats/progress`, {
        headers: this.config.headers
      });
      console.log('User progress response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`Error fetching user progress: ${response.status} ${response.statusText}`);
      }
      
      const data = await handleResponse<any[]>(response);
      console.log('User progress data received:', data);
      
      if (Array.isArray(data) && data.length > 0) {
        // Transform the data if needed to match our component's expectations
        return data.map(item => ({
          userId: item.userId,
          userName: item.userName,
          completedModules: item.completedModules,
          totalModules: item.totalModules,
          completionPercentage: Math.round(item.completionPercentage)
        }));
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching user progress:', error);
      
      // Return mock data as fallback
      console.log('Returning mock user progress data');
      return [
        {
          userId: "mock-user-1",
          userName: "Jane Smith",
          completedModules: 3,
          totalModules: 3,
          completionPercentage: 100
        },
        {
          userId: "mock-user-2",
          userName: "John Doe",
          completedModules: 2,
          totalModules: 3,
          completionPercentage: 67
        },
        {
          userId: "mock-user-3",
          userName: "Alex Johnson",
          completedModules: 1,
          totalModules: 3,
          completionPercentage: 33
        }
      ];
    }
  }
}